/* Swiss Watches Dot Com: ambient video belt for the social section.
   The track drifts on its own and wraps forever. Drag, swipe, horizontal wheel and arrow keys
   move it; it eases back to the drift afterwards. Videos only load and play while on screen. */
(() => {
  if (customElements.get('sw-reels')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class SwReels extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.sw-reels__viewport');
      this.track = this.querySelector('.sw-reels__track');
      if (!this.viewport || !this.track) return;

      this.originals = Array.from(this.track.children);
      if (!this.originals.length) return;

      this.speed = Number(this.dataset.speed) || 30;
      this.x = 0;
      this.velocity = 0;
      this.loopWidth = 0;
      this.hovered = false;
      this.focused = false;
      this.visible = false;
      this.drag = null;
      this.keyTarget = null;
      this.lastFrame = 0;
      this.lastProximity = 0;
      this.frame = 0;

      this.tick = this.tick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);

      this.build();

      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.build(), 120);
      });
      this.resizeObserver.observe(this.viewport);

      this.sectionObserver = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.syncRunning();
        this.updateVideoProximity(true);
      });
      this.sectionObserver.observe(this);

      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
      this.viewport.addEventListener('keydown', this.onKeyDown);
      this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
      this.viewport.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.hovered = true;
      });
      this.viewport.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.hovered = false;
      });
      this.viewport.addEventListener('focusin', () => (this.focused = true));
      this.viewport.addEventListener('focusout', () => (this.focused = false));
      document.addEventListener('visibilitychange', this.onVisibility);
      reducedMotion.addEventListener('change', this.onMotionChange);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      clearTimeout(this.resizeTimer);
      this.resizeObserver?.disconnect();
      this.sectionObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      reducedMotion.removeEventListener('change', this.onMotionChange);
      if (this.autoplayUnlock) {
        window.removeEventListener('pointerdown', this.autoplayUnlock);
        this.autoplayUnlock = null;
      }
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    /* Clone whole sets until the track covers one loop plus a full viewport, so wrapping is seamless. */
    build() {
      this.track.querySelectorAll('[data-sw-clone]').forEach((node) => node.remove());

      const first = this.originals[0];
      const last = this.originals[this.originals.length - 1];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.loopWidth = last.offsetLeft + last.offsetWidth + gap - first.offsetLeft;
      this.step = first.offsetWidth + gap;
      if (this.loopWidth <= 0) return;

      const needed = Math.ceil(this.viewport.clientWidth / this.loopWidth) + 1;
      for (let copy = 0; copy < needed; copy += 1) {
        this.originals.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('data-sw-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('video').forEach((video) => {
            video.removeAttribute('src');
            video.classList.remove('is-playing');
          });
          this.track.appendChild(clone);
        });
      }

      this.render();
      this.updateVideoProximity(true);
    }

    /* IntersectionObserver misses targets inside a translated track on some desktop browsers.
       Measure cards against the viewport box instead, and cap how many decode at once. */
    updateVideoProximity(force = false) {
      const now = performance.now();
      if (!force && now - this.lastProximity < 120) return;
      this.lastProximity = now;

      const root = this.viewport.getBoundingClientRect();
      const pad = 8;
      const left = root.left + pad;
      const right = root.right - pad;
      const top = root.top + pad;
      const bottom = root.bottom - pad;
      const centerX = (root.left + root.right) / 2;
      const maxPlaying = root.width >= 750 ? 7 : 4;
      const candidates = [];

      this.track.querySelectorAll('video').forEach((video) => {
        video.removeAttribute('data-sw-near');
        const card = video.closest('.sw-reels__card');
        if (!card) return;
        const rect = card.getBoundingClientRect();
        if (rect.right <= left || rect.left >= right || rect.bottom <= top || rect.top >= bottom) return;
        const cardCenter = (rect.left + rect.right) / 2;
        candidates.push({ video, distance: Math.abs(cardCenter - centerX) });
      });

      candidates
        .sort((a, b) => a.distance - b.distance)
        .slice(0, maxPlaying)
        .forEach(({ video }) => video.setAttribute('data-sw-near', ''));

      this.syncVideos();
    }

    render() {
      if (!this.loopWidth) return;
      const offset = ((this.x % this.loopWidth) + this.loopWidth) % this.loopWidth;
      this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    }

    targetVelocity() {
      if (reducedMotion.matches || this.hovered || this.focused) return 0;
      return this.speed;
    }

    syncRunning() {
      const run = this.visible && !document.hidden;
      if (run && !this.frame) {
        this.lastFrame = performance.now();
        this.frame = requestAnimationFrame(this.tick);
      } else if (!run && this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
      }
    }

    tick(now) {
      const dt = Math.min((now - this.lastFrame) / 1000, 0.05);
      this.lastFrame = now;

      if (!this.drag && this.keyTarget !== null && this.keyTarget !== undefined) {
        const remaining = this.keyTarget - this.x;
        this.x += remaining * (1 - Math.exp(-9 * dt));
        if (Math.abs(remaining) < 0.5) {
          this.x = this.keyTarget;
          this.keyTarget = null;
        }
        this.render();
      } else if (!this.drag) {
        const target = this.targetVelocity();
        /* A flick decays slowly; settling into the drift or a hover stop is quicker. */
        const rate = Math.abs(this.velocity) > Math.abs(target) + 40 ? 2.2 : 3.2;
        this.velocity += (target - this.velocity) * (1 - Math.exp(-rate * dt));
        this.x += this.velocity * dt;
        this.render();
      }

      this.updateVideoProximity();

      this.frame = requestAnimationFrame(this.tick);
    }

    onPointerDown(event) {
      if (event.button !== 0) return;
      this.drag = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        startOffset: this.x,
        moved: false,
        samples: [{ x: event.clientX, t: performance.now() }],
        type: event.pointerType,
      };
      this.velocity = 0;
      this.keyTarget = null;
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;

      if (!drag.moved) {
        if (Math.abs(dx) < 4) return;
        if (drag.type !== 'mouse' && Math.abs(dy) > Math.abs(dx)) {
          this.endDrag(false);
          return;
        }
        drag.moved = true;
        this.classList.add('is-dragging');
        try {
          this.viewport.setPointerCapture(drag.id);
        } catch (error) {
          /* Capture is best effort. */
        }
      }

      const now = performance.now();
      drag.samples.push({ x: event.clientX, t: now });
      while (drag.samples.length > 2 && now - drag.samples[0].t > 100) drag.samples.shift();
      this.x = drag.startOffset - dx;
      this.render();
    }

    onPointerUp(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      this.endDrag(event.type !== 'pointercancel');
    }

    endDrag(withMomentum) {
      const drag = this.drag;
      this.drag = null;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      if (!drag) return;

      if (withMomentum && drag.moved && drag.samples.length > 1) {
        const firstSample = drag.samples[0];
        const lastSample = drag.samples[drag.samples.length - 1];
        const elapsed = Math.max((lastSample.t - firstSample.t) / 1000, 0.016);
        const flick = -(lastSample.x - firstSample.x) / elapsed;
        this.velocity = Math.max(-3200, Math.min(3200, flick));
      }

      /* Let the click that follows a drag fall through harmlessly, then drop the grabbing cursor. */
      requestAnimationFrame(() => this.classList.remove('is-dragging'));
    }

    onWheel(event) {
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
      if (!horizontal) return;
      event.preventDefault();
      this.velocity = 0;
      this.keyTarget = null;
      this.x += horizontal;
      this.render();
    }

    onKeyDown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.velocity = 0;
      this.keyTarget = (this.keyTarget ?? this.x) + direction * this.step;
    }

    syncVideos() {
      this.track.querySelectorAll('video').forEach((video) => this.syncVideo(video));
    }

    syncVideo(video) {
      const shouldPlay =
        this.visible && !document.hidden && !reducedMotion.matches && video.hasAttribute('data-sw-near');

      if (!shouldPlay) {
        if (!video.paused) video.pause();
        return;
      }

      if (!video.getAttribute('src') && video.dataset.src) {
        video.src = video.dataset.src;
        video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
      }

      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          /* Safari desktop sometimes blocks the first autoplay batch until a gesture. */
          if (!this.autoplayUnlock) {
            this.autoplayUnlock = () => {
              this.autoplayUnlock = null;
              this.updateVideoProximity(true);
            };
            window.addEventListener('pointerdown', this.autoplayUnlock, { once: true, passive: true });
          }
        });
      }
    }

    onVisibility() {
      this.syncRunning();
      this.syncVideos();
    }

    onMotionChange() {
      this.syncVideos();
    }
  }

  customElements.define('sw-reels', SwReels);

  /* Theme editor: bring a selected Video block to the middle of the strip. */
  document.addEventListener('shopify:block:select', (event) => {
    const reels = event.target.closest('sw-reels');
    if (!reels || !reels.loopWidth) return;
    const item = event.target.closest('.sw-reels__item');
    if (!item) return;
    reels.x = item.offsetLeft - reels.viewport.clientWidth / 2 + item.offsetWidth / 2;
    reels.velocity = 0;
    reels.render();
  });
})();
